import { useState } from 'react'

interface UseOtherPanelStateProps {
  // Zoom data
  frontLeftZoomData: number | undefined
  frontCenterZoomData: number | undefined
  frontRightZoomData: number | undefined

  // Aperture data
  frontLeftApertureData: number | undefined
  frontCenterApertureData: number | undefined
  frontRightApertureData: number | undefined

  // Infrared data
  frontLeftIrData: number | undefined
  frontRightIrData: number | undefined

  // Mutation functions
  mutateFrontLeftZoom: (value: number) => void
  mutateFrontCenterZoom: (value: number) => void
  mutateFrontRightZoom: (value: number) => void
  mutateFrontLeftAperture: (value: number) => void
  mutateFrontCenterAperture: (value: number) => void
  mutateFrontRightAperture: (value: number) => void
  mutateFrontLeftIr: (value: number) => void
  mutateFrontRightIr: (value: number) => void
}

export const useOtherPanelState = ({
  frontLeftZoomData,
  frontCenterZoomData,
  frontRightZoomData,
  frontLeftApertureData,
  frontCenterApertureData,
  frontRightApertureData,
  frontLeftIrData,
  frontRightIrData,
  mutateFrontLeftZoom,
  mutateFrontCenterZoom,
  mutateFrontRightZoom,
  mutateFrontLeftAperture,
  mutateFrontCenterAperture,
  mutateFrontRightAperture,
  mutateFrontLeftIr,
  mutateFrontRightIr,
}: UseOtherPanelStateProps) => {
  // Increment states
  const [flIncrement, setFlIncrement] = useState<number>(50)
  const [fcIncrement, setFcIncrement] = useState<number>(50)
  const [frIncrement, setFrIncrement] = useState<number>(50)

  const [isEditingFlZoom, setIsEditingFlZoom] = useState<boolean>(false)
  const [isEditingFcZoom, setIsEditingFcZoom] = useState<boolean>(false)
  const [isEditingFrZoom, setIsEditingFrZoom] = useState<boolean>(false)

  const [isEditingFlAperture, setIsEditingFlAperture] = useState<boolean>(false)
  const [isEditingFcAperture, setIsEditingFcAperture] = useState<boolean>(false)
  const [isEditingFrAperture, setIsEditingFrAperture] = useState<boolean>(false)

  const [isEditingFlIr, setIsEditingFlIr] = useState<boolean>(false)
  const [isEditingFrIr, setIsEditingFrIr] = useState<boolean>(false)

  // Aperture increment states
  const [flApertureIncrement, setFlApertureIncrement] = useState<number>(5)
  const [fcApertureIncrement, setFcApertureIncrement] = useState<number>(5)
  const [frApertureIncrement, setFrApertureIncrement] = useState<number>(5)

  // Infrared increment states - smaller steps for 0-255 range
  const [flIrIncrement, setFlIrIncrement] = useState<number>(5)
  const [frIrIncrement, setFrIrIncrement] = useState<number>(5)

  // Save button states
  const [displayFlSaveButton, setDisplayFlSaveButton] = useState<boolean>(false)
  const [displayFrSaveButton, setDisplayFrSaveButton] = useState<boolean>(false)
  const [displayFcSaveButton, setDisplayFcSaveButton] = useState<boolean>(false)

  // Aperture save button states
  const [displayFlApertureSaveButton, setDisplayFlApertureSaveButton] = useState<boolean>(false)
  const [displayFrApertureSaveButton, setDisplayFrApertureSaveButton] = useState<boolean>(false)
  const [displayFcApertureSaveButton, setDisplayFcApertureSaveButton] = useState<boolean>(false)

  // Infrared save button states
  const [displayFlIrSaveButton, setDisplayFlIrSaveButton] = useState<boolean>(false)
  const [displayFrIrSaveButton, setDisplayFrIrSaveButton] = useState<boolean>(false)

  // Pending values for confirmation cases
  const [pendingFlZoomValue, setPendingFlZoomValue] = useState<number | null>(null)
  const [pendingFcZoomValue, setPendingFcZoomValue] = useState<number | null>(null)
  const [pendingFrZoomValue, setPendingFrZoomValue] = useState<number | null>(null)

  const [pendingFlApertureValue, setPendingFlApertureValue] = useState<number | null>(null)
  const [pendingFcApertureValue, setPendingFcApertureValue] = useState<number | null>(null)
  const [pendingFrApertureValue, setPendingFrApertureValue] = useState<number | null>(null)

  const [pendingFlIrValue, setPendingFlIrValue] = useState<number | null>(null)
  const [pendingFrIrValue, setPendingFrIrValue] = useState<number | null>(null)

  // Threshold constants for determining when to show save buttons
  const ZOOM_THRESHOLD = 50
  const APERTURE_THRESHOLD = 10
  const INFRARED_THRESHOLD = 20

  const shouldMutateImmediately = (
    newValue: number,
    currentValue: number | undefined,
    threshold: number,
    pendingValue: number | null
  ) => {
    // Use pending value if it exists, otherwise use current server value
    const baseValue = pendingValue ?? currentValue ?? 0
    const changeAmount = Math.abs(newValue - baseValue)
    return changeAmount <= threshold
  }

  // Zoom change handlers
  const handleFrontLeftZoomChange = (value: number) => {
    if (isEditingFlZoom) {
      setPendingFlZoomValue(value)
      setDisplayFlSaveButton(true)
      return
    }

    if (shouldMutateImmediately(value, frontLeftZoomData, ZOOM_THRESHOLD, pendingFlZoomValue)) {
      mutateFrontLeftZoom(value)
      setDisplayFlSaveButton(false)
      setPendingFlZoomValue(null)
    } else {
      setDisplayFlSaveButton(true)
      setPendingFlZoomValue(value)
    }
  }

  const handleFrontCenterZoomChange = (value: number) => {
    if (isEditingFcZoom) {
      setPendingFcZoomValue(value)
      setDisplayFcSaveButton(true)
      return
    }

    if (shouldMutateImmediately(value, frontCenterZoomData, ZOOM_THRESHOLD, pendingFcZoomValue)) {
      mutateFrontCenterZoom(value)
      setDisplayFcSaveButton(false)
      setPendingFcZoomValue(null)
    } else {
      setDisplayFcSaveButton(true)
      setPendingFcZoomValue(value)
    }
  }

  const handleFrontRightZoomChange = (value: number) => {
    if (isEditingFrZoom) {
      setPendingFrZoomValue(value)
      setDisplayFrSaveButton(true)
      return
    }

    if (shouldMutateImmediately(value, frontRightZoomData, ZOOM_THRESHOLD, pendingFrZoomValue)) {
      mutateFrontRightZoom(value)
      setDisplayFrSaveButton(false)
      setPendingFrZoomValue(null)
    } else {
      setDisplayFrSaveButton(true)
      setPendingFrZoomValue(value)
    }
  }

  // Aperture change handlers
  const handleFrontLeftApertureChange = (value: number) => {
    if (isEditingFlAperture) {
      setPendingFlApertureValue(value)
      setDisplayFlApertureSaveButton(true)
      return
    }

    if (shouldMutateImmediately(value, frontLeftApertureData, APERTURE_THRESHOLD, pendingFlApertureValue)) {
      mutateFrontLeftAperture(value)
      setDisplayFlApertureSaveButton(false)
      setPendingFlApertureValue(null)
    } else {
      setDisplayFlApertureSaveButton(true)
      setPendingFlApertureValue(value)
    }
  }

  const handleFrontCenterApertureChange = (value: number) => {
    if (isEditingFcAperture) {
      setPendingFcApertureValue(value)
      setDisplayFcApertureSaveButton(true)
      return
    }

    if (shouldMutateImmediately(value, frontCenterApertureData, APERTURE_THRESHOLD, pendingFcApertureValue)) {
      mutateFrontCenterAperture(value)
      setDisplayFcApertureSaveButton(false)
      setPendingFcApertureValue(null)
    } else {
      setDisplayFcApertureSaveButton(true)
      setPendingFcApertureValue(value)
    }
  }

  const handleFrontRightApertureChange = (value: number) => {
    if (isEditingFrAperture) {
      setPendingFrApertureValue(value)
      setDisplayFrApertureSaveButton(true)
      return
    }

    if (shouldMutateImmediately(value, frontRightApertureData, APERTURE_THRESHOLD, pendingFrApertureValue)) {
      mutateFrontRightAperture(value)
      setDisplayFrApertureSaveButton(false)
      setPendingFrApertureValue(null)
    } else {
      setDisplayFrApertureSaveButton(true)
      setPendingFrApertureValue(value)
    }
  }

  // Infrared change handlers
  const handleFrontLeftIrChange = (value: number) => {
    if (isEditingFlIr) {
      setPendingFlIrValue(value)
      setDisplayFlIrSaveButton(true)
      return
    }

    if (shouldMutateImmediately(value, frontLeftIrData, INFRARED_THRESHOLD, pendingFlIrValue)) {
      mutateFrontLeftIr(value)
      setDisplayFlIrSaveButton(false)
      setPendingFlIrValue(null)
    } else {
      setDisplayFlIrSaveButton(true)
      setPendingFlIrValue(value)
    }
  }

  const handleFrontRightIrChange = (value: number) => {
    if (isEditingFrIr) {
      setPendingFrIrValue(value)
      setDisplayFrIrSaveButton(true)
      return
    }

    if (shouldMutateImmediately(value, frontRightIrData, INFRARED_THRESHOLD, pendingFrIrValue)) {
      mutateFrontRightIr(value)
      setDisplayFrIrSaveButton(false)
      setPendingFrIrValue(null)
    } else {
      setDisplayFrIrSaveButton(true)
      setPendingFrIrValue(value)
    }
  }

  // Save button handlers that perform mutations
  const handleFrontLeftZoomSave = () => {
    if (pendingFlZoomValue !== null) {
      mutateFrontLeftZoom(pendingFlZoomValue)
      setDisplayFlSaveButton(false)
      setPendingFlZoomValue(null)
    }
  }

  const handleFrontCenterZoomSave = () => {
    if (pendingFcZoomValue !== null) {
      mutateFrontCenterZoom(pendingFcZoomValue)
      setDisplayFcSaveButton(false)
      setPendingFcZoomValue(null)
    }
  }

  const handleFrontRightZoomSave = () => {
    if (pendingFrZoomValue !== null) {
      mutateFrontRightZoom(pendingFrZoomValue)
      setDisplayFrSaveButton(false)
      setPendingFrZoomValue(null)
    }
  }

  const handleFrontLeftApertureSave = () => {
    if (pendingFlApertureValue !== null) {
      mutateFrontLeftAperture(pendingFlApertureValue)
      setDisplayFlApertureSaveButton(false)
      setPendingFlApertureValue(null)
    }
  }

  const handleFrontCenterApertureSave = () => {
    if (pendingFcApertureValue !== null) {
      mutateFrontCenterAperture(pendingFcApertureValue)
      setDisplayFcApertureSaveButton(false)
      setPendingFcApertureValue(null)
    }
  }

  const handleFrontRightApertureSave = () => {
    if (pendingFrApertureValue !== null) {
      mutateFrontRightAperture(pendingFrApertureValue)
      setDisplayFrApertureSaveButton(false)
      setPendingFrApertureValue(null)
    }
  }

  const handleFrontLeftIrSave = () => {
    if (pendingFlIrValue !== null) {
      mutateFrontLeftIr(pendingFlIrValue)
      setDisplayFlIrSaveButton(false)
      setPendingFlIrValue(null)
    }
  }

  const handleFrontRightIrSave = () => {
    if (pendingFrIrValue !== null) {
      mutateFrontRightIr(pendingFrIrValue)
      setDisplayFrIrSaveButton(false)
      setPendingFrIrValue(null)
    }
  }

  return {
    // Increment states
    flIncrement,
    setFlIncrement,
    fcIncrement,
    setFcIncrement,
    frIncrement,
    setFrIncrement,
    flApertureIncrement,
    setFlApertureIncrement,
    fcApertureIncrement,
    setFcApertureIncrement,
    frApertureIncrement,
    setFrApertureIncrement,
    flIrIncrement,
    setFlIrIncrement,
    frIrIncrement,
    setFrIrIncrement,

    // Save button states
    displayFlSaveButton,
    displayFrSaveButton,
    displayFcSaveButton,
    displayFlApertureSaveButton,
    displayFrApertureSaveButton,
    displayFcApertureSaveButton,
    displayFlIrSaveButton,
    displayFrIrSaveButton,

    // Pending values
    pendingFlZoomValue,
    pendingFcZoomValue,
    pendingFrZoomValue,
    pendingFlApertureValue,
    pendingFcApertureValue,
    pendingFrApertureValue,
    pendingFlIrValue,
    pendingFrIrValue,

    // Change handlers
    handleFrontLeftZoomChange,
    handleFrontCenterZoomChange,
    handleFrontRightZoomChange,
    handleFrontLeftApertureChange,
    handleFrontCenterApertureChange,
    handleFrontRightApertureChange,
    handleFrontLeftIrChange,
    handleFrontRightIrChange,

    // Save handlers
    handleFrontLeftZoomSave,
    handleFrontCenterZoomSave,
    handleFrontRightZoomSave,
    handleFrontLeftApertureSave,
    handleFrontCenterApertureSave,
    handleFrontRightApertureSave,
    handleFrontLeftIrSave,
    handleFrontRightIrSave,

    // Editing states
    isEditingFlZoom,
    isEditingFcZoom,
    isEditingFrZoom,
    isEditingFlAperture,
    isEditingFcAperture,
    isEditingFrAperture,
    isEditingFlIr,
    isEditingFrIr,

    // Set editing states
    setIsEditingFlZoom,
    setIsEditingFcZoom,
    setIsEditingFrZoom,
    setIsEditingFlAperture,
    setIsEditingFcAperture,
    setIsEditingFrAperture,
    setIsEditingFlIr,
    setIsEditingFrIr,
  }
}
